var __rest = (this && this.__rest) || function (s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
};
import { Category } from '@antv/gui';
import { format } from 'd3-format';
import { useLibrary } from '../runtime/library';
import { domainOf, G2Layout, inferComponentLayout, inferComponentShape, scaleOf, titleContent, } from './utils';
function inferLayout(position, gridRow, gridCol) {
    const [gridRowLimit, gridColLimit] = [gridRow || 100, gridCol || 100];
    switch (position) {
        case 'top':
        case 'bottom':
        case 'top-left':
        case 'top-right':
        case 'bottom-left':
        case 'bottom-right':
            return [1, gridColLimit];
        case 'left':
        case 'right':
            return [gridRowLimit, 1];
        default:
            return [gridRow, gridCol];
    }
}
function createShape(shape, library, coordinate, theme, style = {}) {
    const [useShape] = useLibrary('shape', library);
    return () => useShape({ type: `point.${shape}` })([
        [0, 0],
        [0, 0],
    ], Object.assign({ size: 6 }, style), coordinate, theme);
}
function inferItemMarker(scales, options, library, coordinate, theme) {
    const shapeScale = scaleOf(scales, 'shape');
    const { itemMarker } = options;
    if (shapeScale && !itemMarker) {
        const { domain } = shapeScale.getOptions();
        return (d, i) => createShape(shapeScale.map(domain[i]), library, coordinate, theme, {
            color: d.color,
        });
    }
    if (typeof itemMarker === 'function') {
        return itemMarker;
    }
    return (d, i) => createShape(itemMarker || 'point', library, coordinate, theme, {
        color: d.color,
    });
}
function inferItemMarkerOpacity(scales) {
    const scale = scaleOf(scales, 'opacity');
    if (scale) {
        const { range } = scale.getOptions();
        return (d, i) => range[i];
    }
    return undefined;
}
function inferItemMarkerSize(scales) {
    const scale = scaleOf(scales, 'size');
    // only support constant size scale.
    // size in category legend means the marker radius.
    if (scale)
        return scale.map(NaN) * 2;
    return 8;
}
function inferCategoryStyle(scales, options, library, coordinate, theme) {
    const { labelFormatter = (d) => `${d}` } = options;
    const baseStyle = {
        itemMarker: inferItemMarker(scales, options, library, coordinate, theme),
        itemMarkerSize: inferItemMarkerSize(scales),
        itemMarkerOpacity: inferItemMarkerOpacity(scales),
    };
    const finalLabelFormatter = typeof labelFormatter === 'string'
        ? format(labelFormatter)
        : labelFormatter;
    // here must exists a color scale
    const colorScale = scaleOf(scales, 'color');
    const domain = domainOf(scales);
    return Object.assign(Object.assign({}, baseStyle), { data: domain.map((d) => ({
            id: d,
            label: finalLabelFormatter(d),
            color: colorScale.map(d),
        })) });
}
function inferLegendShape(value, options, component) {
    const { position } = options;
    if (position === 'center') {
        const { bbox } = value;
        // to be comfirm: if position is center, we should use the width and height of user definition.
        const { width, height } = bbox;
        return { width, height };
    }
    const { width, height } = inferComponentShape(value, options, component);
    return { width, height };
}
/**
 * Guide Component for ordinal color scale.
 */
export const LegendCategory = (options) => {
    const { order, size, position, orientation, itemMarker, labelFormatter, dx = 0, dy = 0, title, gridCol, gridRow, layout } = options, rest = __rest(options, ["order", "size", "position", "orientation", "itemMarker", "labelFormatter", "dx", "dy", "title", "gridCol", "gridRow", "layout"]);
    return (scales, value, coordinate, theme) => {
        var _a, _b, _c;
        const { library, bbox } = value;
        const { x, y } = bbox;
        const { width, height } = inferLegendShape(value, options, LegendCategory);
        const finalLayout = inferComponentLayout(position, (_c = (_b = (_a = value.scales) === null || _a === void 0 ? void 0 : _a[0]) === null || _b === void 0 ? void 0 : _b.guide) === null || _c === void 0 ? void 0 : _c.layout);
        const [finalGridRow, finalGridCol] = inferLayout(position, gridRow, gridCol);
        const legendStyle = Object.assign({ orient: ['right', 'left', 'center'].includes(position)
                ? 'vertical'
                : 'horizontal', width,
            height, gridCol: gridCol !== null && gridCol !== void 0 ? gridCol : finalGridCol, gridRow: gridRow !== null && gridRow !== void 0 ? gridRow : finalGridRow, rowPadding: 0, colPadding: 8, titleText: titleContent(title) }, inferCategoryStyle(scales, options, library, coordinate, theme));
        const { legend: legendTheme = {} } = theme;
        const layoutWrapper = new G2Layout({
            style: Object.assign({ x: x + dx, y: y + dy, width: bbox.width, height: bbox.height }, finalLayout),
        });
        layoutWrapper.appendChild(new Category({
            className: 'legend-category',
            style: Object.assign({}, legendTheme, legendStyle, rest),
        }));
        return layoutWrapper;
    };
};
LegendCategory.props = {
    defaultPosition: 'top',
    defaultOrder: 1,
    defaultSize: 40,
};
//# sourceMappingURL=legendCategory.js.map